import { useState } from 'react';
import { post, errorMessage } from '../api';
import { Field } from '../ui/controls';
import type { User } from '../library';

export function Login({ onDone }: { onDone: (u: User) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthCard title="Sign in to Calendar Studio">
      <form className="col" onSubmit={async (e) => {
        e.preventDefault(); setBusy(true); setErr(null);
        try { onDone((await post<{ user: User }>('/api/auth/login', { email, password })).user); } catch (x) { setErr(errorMessage(x)); } finally { setBusy(false); }
      }}>
        <Field label="Email"><input className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
        <Field label="Password"><input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></Field>
        {err && <div className="notice err" role="alert">{err}</div>}
        <button className="btn primary" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
        <p className="small faint">Forgotten password? Ask an administrator to reset it under Users.</p>
      </form>
    </AuthCard>
  );
}

export function Setup({ onDone }: { onDone: (u: User) => void }) {
  const [f, setF] = useState({ token: '', name: '', email: '', password: '', confirm: '' });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = f.confirm && f.password !== f.confirm;
  return (
    <AuthCard title="Set up Calendar Studio">
      <p className="muted">Create the first administrator account. You need the one-time setup token shown in the server log (or the <code>SETUP_TOKEN</code> environment variable).</p>
      <form className="col" onSubmit={async (e) => {
        e.preventDefault(); if (mismatch) return; setBusy(true); setErr(null);
        try { onDone((await post<{ user: User }>('/api/setup', f)).user); } catch (x) { setErr(errorMessage(x)); } finally { setBusy(false); }
      }}>
        <Field label="Setup token"><input className="input mono" value={f.token} onChange={(e) => setF({ ...f, token: e.target.value })} required /></Field>
        <Field label="Your name"><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
        <Field label="Email"><input className="input" type="email" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></Field>
        <Field label="Password" hint="At least 10 characters"><input className="input" type="password" autoComplete="new-password" minLength={10} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required /></Field>
        <Field label="Confirm password" hint={mismatch ? <span style={{ color: 'var(--err)' }}>Passwords do not match</span> : undefined}><input className="input" type="password" autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} required /></Field>
        {err && <div className="notice err" role="alert">{err}</div>}
        <button className="btn primary" disabled={busy || !!mismatch}>{busy ? 'Creating…' : 'Create administrator'}</button>
      </form>
    </AuthCard>
  );
}

function AuthCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'linear-gradient(160deg, #1f2a37, #3b4a5c)' }}>
      <div className="card pad" style={{ width: '100%', maxWidth: 400 }}>
        <div className="row" style={{ marginBottom: 16 }}><img src="/favicon.svg" width={28} height={28} alt="" /><strong>Print Station · Calendar Studio</strong></div>
        <h1 style={{ marginBottom: 12 }}>{title}</h1>
        {children}
      </div>
    </div>
  );
}
